import { z } from 'zod';
import type { ShapeManifest } from '../../../lib/diagram/shape-manifest.types';
import { resolveMediaSrc } from '../../media/utils/resolveMediaSrc';

const pdfShapePropsSchema = z.object({
  objectKey: z.string().default(''),
  name: z.string().default('Untitled PDF'),
  description: z.string().optional(),
});

export type PdfShapeProps = z.infer<typeof pdfShapePropsSchema>;

export const PdfShapeManifest: ShapeManifest<PdfShapeProps> = {
  id: 'pdf',
  version: '1.0.0',
  category: 'canvas',
  icon: 'file-text',
  propsSchema: pdfShapePropsSchema,
  defaultProps: () => ({
    objectKey: '',
    name: 'Untitled PDF',
    description: '',
  }),
  defaultDimensions: { width: 180, height: 96 },
  anchors: {
    snap: ['top', 'right', 'bottom', 'left', 'center'],
    perimeter: 'rect',
  },
  capabilities: {
    selectable: true,
    transformable: true,
    textEditable: false,
    connectable: true,
  },
  properties: [
    { key: 'objectKey', label: 'PDF', kind: 'string', required: true, ui: { group: 'identity', priority: 1 } },
    { key: 'name', label: 'Name', kind: 'string', ui: { group: 'identity', priority: 2 } },
    { key: 'description', label: 'Description', kind: 'string', ui: { group: 'context', priority: 3, control: 'textarea' } },
  ],
  acl: () => true,
  renderers: {
    svg: ({ viewModel, props }) => {
      const width = Number(viewModel?.width ?? 0);
      const height = Number(viewModel?.height ?? 0);
      const safeWidth = Number.isFinite(width) && width > 0 ? width : 1;
      const safeHeight = Number.isFinite(height) && height > 0 ? height : 1;
      const propsRecord = props as unknown as Record<string, unknown>;
      const attrs = (propsRecord.attrs as Record<string, unknown> | undefined) ?? {};
      const objectKey =
        typeof propsRecord.objectKey === 'string'
          ? propsRecord.objectKey
          : typeof attrs.objectKey === 'string'
            ? attrs.objectKey
            : '';
      const name =
        typeof propsRecord.name === 'string'
          ? propsRecord.name
          : typeof attrs.name === 'string'
            ? attrs.name
            : 'Untitled PDF';
      const href = resolveMediaSrc(objectKey);
      const escapedHref = href
        .replace(/&/g, '&amp;')
        .replace(/"/g, '&quot;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/'/g, '&apos;');
      const escapedName = name
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
        .replace(/'/g, '&apos;');

      return [
        `<a href="${escapedHref}" target="_blank">`,
        `<rect x="0" y="0" width="${safeWidth}" height="${safeHeight}" rx="10" ry="10" fill="#FFFFFF" stroke="#E2E8F0" stroke-width="1" />`,
        `<rect x="12" y="12" width="40" height="48" rx="8" ry="8" fill="#DC2626" stroke="#B91C1C" stroke-width="1" />`,
        `<text x="32" y="40" text-anchor="middle" fill="#FFFFFF" font-size="11" font-weight="700" font-family="Noto Sans, sans-serif">PDF</text>`,
        `<text x="60" y="36" fill="#0F172A" font-size="14" font-weight="700" font-family="Noto Sans, sans-serif">${escapedName}</text>`,
        `</a>`,
      ].join('');
    },
  },
};
